import React, { useState, useMemo } from "react";
import {
  PROJECTS, WORKERS, INITIAL_SUPPLIERS, CLIENTS,
  INITIAL_TRANSACTIONS, TRANSACTION_CATEGORIES, PAYMENT_MODES
} from "../data/appData";

const formatINR = (n) => "₹" + Number(n).toLocaleString("en-IN");

const emptyForm = {
  date: new Date().toISOString().split("T")[0],
  projectId: "",
  type: "Cash In",
  category: "",
  fromTo: "",
  amount: "",
  paymentMode: "",
  reference: "",
  remarks: "",
};

function TransactionMaster() {
  const [transactions, setTransactions] = useState(INITIAL_TRANSACTIONS);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [viewTxn, setViewTxn] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Filters
  const [filterProject, setFilterProject] = useState("");
  const [filterType, setFilterType] = useState("All");
  const [filterCategory, setFilterCategory] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...formData, [name]: value };
    if (name === "type") updated.category = "";
    if (name === "projectId") {
      const proj = PROJECTS.find(p => p.id === Number(value));
      updated.projectName = proj ? proj.name : "";
    }
    setFormData(updated);
    if (errors[name]) setErrors({ ...errors, [name]: "" });
  };

  const validate = () => {
    const e = {};
    if (!formData.date) e.date = "Date required";
    if (!formData.projectId) e.projectId = "Select project";
    if (!formData.category) e.category = "Select category";
    if (!formData.fromTo.trim()) e.fromTo = "From/To required";
    if (!formData.amount || isNaN(formData.amount) || Number(formData.amount) <= 0) e.amount = "Enter valid amount";
    if (!formData.paymentMode) e.paymentMode = "Select payment mode";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const proj = PROJECTS.find(p => p.id === Number(formData.projectId));
    const txn = {
      ...formData,
      amount: Number(formData.amount),
      projectId: Number(formData.projectId),
      projectName: proj ? proj.name : "",
    };
    if (editId) {
      setTransactions(transactions.map(t => t.id === editId ? { ...txn, id: editId } : t));
    } else {
      setTransactions([...transactions, { ...txn, id: Date.now() }]);
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setErrors({});
    setEditId(null);
    setShowForm(false);
  };

  const handleEdit = (txn) => {
    setFormData({ ...txn, projectId: String(txn.projectId) });
    setEditId(txn.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = (id) => {
    setTransactions(transactions.filter(t => t.id !== id));
    setDeleteConfirm(null);
  };

  // Filtered transactions
  const filtered = useMemo(() => {
    return transactions.filter(t => {
      if (filterProject && t.projectId !== Number(filterProject)) return false;
      if (filterType !== "All" && t.type !== filterType) return false;
      if (filterCategory && t.category !== filterCategory) return false;
      if (dateFrom && t.date < dateFrom) return false;
      if (dateTo && t.date > dateTo) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!t.remarks?.toLowerCase().includes(q) &&
          !t.fromTo?.toLowerCase().includes(q) &&
          !t.projectName?.toLowerCase().includes(q) &&
          !t.reference?.toLowerCase().includes(q)) return false;
      }
      return true;
    }).sort((a, b) => b.date.localeCompare(a.date));
  }, [transactions, filterProject, filterType, filterCategory, dateFrom, dateTo, search]);

  // Totals
  const totalIn = filtered.filter(t => t.type === "Cash In").reduce((s, t) => s + t.amount, 0);
  const totalOut = filtered.filter(t => t.type === "Cash Out").reduce((s, t) => s + t.amount, 0);
  const balance = totalIn - totalOut;

  // Project summary for selected project
  const projectSummary = useMemo(() => {
    if (!filterProject) return null;
    const proj = PROJECTS.find(p => p.id === Number(filterProject));
    if (!proj) return null;
    const projTxns = transactions.filter(t => t.projectId === Number(filterProject));
    const received = projTxns.filter(t => t.type === "Cash In").reduce((s, t) => s + t.amount, 0);
    const spent = projTxns.filter(t => t.type === "Cash Out").reduce((s, t) => s + t.amount, 0);
    return {
      name: proj.name,
      client: proj.client,
      budget: proj.budget,
      received,
      spent,
      balance: received - spent,
      budgetUsed: ((spent / proj.budget) * 100).toFixed(1),
    };
  }, [filterProject, transactions]);

  // Category options for filter
  const allCategories = [...new Set([
    ...TRANSACTION_CATEGORIES["Cash In"],
    ...TRANSACTION_CATEGORIES["Cash Out"]
  ])];

  // Get fromTo suggestions based on category
  const getFromToSuggestions = () => {
    const cat = formData.category;
    if (!cat) return [];
    if (cat === "Labour Payment") return WORKERS.filter(w => w.status === "Active").map(w => w.name);
    if (["Material Purchase", "Supplier Payment"].includes(cat))
      return INITIAL_SUPPLIERS.filter(s => s.status === "Active").map(s => s.supplierName);
    if (["Client Advance", "Client Payment", "Final Settlement"].includes(cat))
      return CLIENTS.map(c => c.name);
    return [];
  };

  const suggestions = getFromToSuggestions();

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h3 className="gold-text mb-0">Transaction Master</h3>
          <small className="text-muted">Payment ledger · {transactions.length} transactions</small>
        </div>
        <button className="btn btn-warning fw-semibold" onClick={() => { setShowForm(!showForm); if (editId) resetForm(); }}>
          <i className={`bi ${showForm && !editId ? "bi-x-lg" : "bi-plus-lg"} me-2`}></i>
          {showForm && !editId ? "Cancel" : "Add Transaction"}
        </button>
      </div>

      {/* Project Summary Banner */}
      {projectSummary && (
        <div className="card mb-4 p-3" style={{ background: "linear-gradient(135deg, #3e2723 0%, #5d4037 100%)", color: "#f5f5dc", border: "none" }}>
          <div className="d-flex justify-content-between flex-wrap gap-2">
            <div>
              <div style={{ fontSize: 13, opacity: 0.8 }}>Project Overview</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{projectSummary.name}</div>
              <div style={{ fontSize: 13, opacity: 0.8 }}>Client: {projectSummary.client}</div>
            </div>
            <div className="d-flex gap-3 flex-wrap">
              {[
                { label: "Budget", value: formatINR(projectSummary.budget), color: "#fff9c4" },
                { label: "Received", value: formatINR(projectSummary.received), color: "#c8e6c9" },
                { label: "Spent", value: formatINR(projectSummary.spent), color: "#ffcdd2" },
                { label: "Net Balance", value: formatINR(projectSummary.balance), color: projectSummary.balance >= 0 ? "#c8e6c9" : "#ffcdd2" },
              ].map((s, i) => (
                <div key={i} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 11, opacity: 0.8 }}>{s.label}</div>
                </div>
              ))}
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#fff9c4" }}>{projectSummary.budgetUsed}%</div>
                <div style={{ fontSize: 11, opacity: 0.8 }}>Budget Used</div>
              </div>
            </div>
          </div>
          {/* Progress Bar */}
          <div className="mt-2">
            <div style={{ background: "rgba(255,255,255,0.2)", borderRadius: 4, height: 6 }}>
              <div style={{
                width: `${Math.min(projectSummary.budgetUsed, 100)}%`,
                background: projectSummary.budgetUsed > 90 ? "#ef9a9a" : "#a5d6a7",
                height: "100%", borderRadius: 4, transition: "width 0.5s"
              }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Summary KPI Cards */}
      <div className="row g-3 mb-4">
        {[
          { label: "Total Cash In", value: formatINR(totalIn), icon: "bi-arrow-down-circle-fill", color: "#2e7d32", bg: "#e8f5e9" },
          { label: "Total Cash Out", value: formatINR(totalOut), icon: "bi-arrow-up-circle-fill", color: "#c62828", bg: "#ffebee" },
          { label: "Net Balance", value: formatINR(balance), icon: "bi-wallet2", color: balance >= 0 ? "#1565c0" : "#c62828", bg: balance >= 0 ? "#e3f2fd" : "#ffebee" },
          { label: "Transactions", value: filtered.length, icon: "bi-receipt", color: "#b8860b", bg: "#fffde7" },
        ].map((kpi, i) => (
          <div className="col-6 col-md-3" key={i}>
            <div className="card p-3" style={{ background: kpi.bg, border: "none" }}>
              <div className="d-flex align-items-center gap-2">
                <i className={`bi ${kpi.icon}`} style={{ fontSize: 28, color: kpi.color }}></i>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: kpi.color }}>{kpi.value}</div>
                  <div className="text-muted" style={{ fontSize: 11 }}>{kpi.label}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add / Edit Form */}
      {showForm && (
        <div className="card p-4 mb-4" style={{ borderTop: `4px solid ${editId ? "#b8860b" : "#2e7d32"}` }}>
          <h5 className="mb-3" style={{ color: editId ? "#b8860b" : "#2e7d32" }}>
            <i className={`bi ${editId ? "bi-pencil-square" : "bi-plus-circle"} me-2`}></i>
            {editId ? "Edit Transaction" : "New Transaction Entry"}
          </h5>
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Date */}
              <div className="col-md-4 col-lg-3 mb-3">
                <label className="form-label fw-semibold">Date *</label>
                <input type="date" className={`form-control ${errors.date ? "is-invalid" : ""}`}
                  name="date" value={formData.date} onChange={handleChange} />
                {errors.date && <div className="invalid-feedback">{errors.date}</div>}
              </div>
              {/* Project */}
              <div className="col-md-4 col-lg-4 mb-3">
                <label className="form-label fw-semibold">Project *</label>
                <select className={`form-select ${errors.projectId ? "is-invalid" : ""}`}
                  name="projectId" value={formData.projectId} onChange={handleChange}>
                  <option value="">Select Project</option>
                  {PROJECTS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                {errors.projectId && <div className="invalid-feedback">{errors.projectId}</div>}
              </div>
              {/* Type */}
              <div className="col-md-4 col-lg-2 mb-3">
                <label className="form-label fw-semibold">Type *</label>
                <div className="d-flex gap-2 mt-1">
                  {["Cash In", "Cash Out"].map(t => (
                    <button key={t} type="button"
                      className={`btn btn-sm flex-fill fw-semibold ${formData.type === t ? (t === "Cash In" ? "btn-success" : "btn-danger") : "btn-outline-secondary"}`}
                      onClick={() => setFormData({ ...formData, type: t, category: "" })}>
                      <i className={`bi ${t === "Cash In" ? "bi-arrow-down" : "bi-arrow-up"} me-1`}></i>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              {/* Category */}
              <div className="col-md-4 col-lg-3 mb-3">
                <label className="form-label fw-semibold">Category *</label>
                <select className={`form-select ${errors.category ? "is-invalid" : ""}`}
                  name="category" value={formData.category} onChange={handleChange}>
                  <option value="">Select Category</option>
                  {(TRANSACTION_CATEGORIES[formData.type] || []).map(c => <option key={c}>{c}</option>)}
                </select>
                {errors.category && <div className="invalid-feedback">{errors.category}</div>}
              </div>
              {/* From / To */}
              <div className="col-md-4 col-lg-3 mb-3">
                <label className="form-label fw-semibold">{formData.type === "Cash In" ? "Received From" : "Paid To"} *</label>
                {suggestions.length > 0 ? (
                  <select className={`form-select ${errors.fromTo ? "is-invalid" : ""}`}
                    name="fromTo" value={formData.fromTo} onChange={handleChange}>
                    <option value="">Select...</option>
                    {suggestions.map(s => <option key={s}>{s}</option>)}
                    <option value="__other__">Other (type below)</option>
                  </select>
                ) : (
                  <input type="text" className={`form-control ${errors.fromTo ? "is-invalid" : ""}`}
                    name="fromTo" value={formData.fromTo} onChange={handleChange}
                    placeholder="Name / Company" />
                )}
                {formData.fromTo === "__other__" && (
                  <input type="text" className="form-control mt-1" placeholder="Enter name..."
                    onChange={e => setFormData({ ...formData, fromTo: e.target.value })} />
                )}
                {errors.fromTo && <div className="invalid-feedback">{errors.fromTo}</div>}
              </div>
              {/* Amount */}
              <div className="col-md-4 col-lg-2 mb-3">
                <label className="form-label fw-semibold">Amount (₹) *</label>
                <input type="number" className={`form-control ${errors.amount ? "is-invalid" : ""}`}
                  name="amount" value={formData.amount} onChange={handleChange} placeholder="0.00" min="1" />
                {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
              </div>
              {/* Payment Mode */}
              <div className="col-md-4 col-lg-2 mb-3">
                <label className="form-label fw-semibold">Payment Mode *</label>
                <select className={`form-select ${errors.paymentMode ? "is-invalid" : ""}`}
                  name="paymentMode" value={formData.paymentMode} onChange={handleChange}>
                  <option value="">Select Mode</option>
                  {PAYMENT_MODES.map(m => <option key={m}>{m}</option>)}
                </select>
                {errors.paymentMode && <div className="invalid-feedback">{errors.paymentMode}</div>}
              </div>
              {/* Reference */}
              <div className="col-md-4 col-lg-2 mb-3">
                <label className="form-label fw-semibold">Reference No.</label>
                <input type="text" className="form-control" name="reference" value={formData.reference}
                  onChange={handleChange} placeholder="TXN / Cheque No." />
              </div>
              {/* Remarks */}
              <div className="col-12 mb-3">
                <label className="form-label fw-semibold">Remarks</label>
                <textarea className="form-control" name="remarks" value={formData.remarks}
                  onChange={handleChange} rows={2} placeholder="Notes about this transaction..." />
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-secondary" onClick={resetForm}>Cancel</button>
              <button type="submit" className={`btn fw-semibold ${editId ? "btn-warning" : (formData.type === "Cash In" ? "btn-success" : "btn-danger")}`}>
                <i className={`bi ${editId ? "bi-check2" : "bi-save"} me-2`}></i>
                {editId ? "Update Transaction" : `Record ${formData.type}`}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="card p-3 mb-3">
        <div className="row g-2 align-items-end">
          {/* Project Filter */}
          <div className="col-md-3 col-6">
            <label className="form-label small fw-semibold mb-1">Project</label>
            <select className="form-select form-select-sm" value={filterProject} onChange={e => setFilterProject(e.target.value)}>
              <option value="">All Projects</option>
              {PROJECTS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          {/* Type Toggle */}
          <div className="col-md-2 col-6">
            <label className="form-label small fw-semibold mb-1">Type</label>
            <div className="btn-group w-100" role="group">
              {["All", "Cash In", "Cash Out"].map(t => (
                <button key={t} type="button"
                  className={`btn btn-sm ${filterType === t ? "btn-warning" : "btn-outline-secondary"}`}
                  style={{ fontSize: 11, padding: "4px 6px" }}
                  onClick={() => setFilterType(t)}>
                  {t === "All" ? "All" : t === "Cash In" ? "IN" : "OUT"}
                </button>
              ))}
            </div>
          </div>
          {/* Category */}
          <div className="col-md-2 col-6">
            <label className="form-label small fw-semibold mb-1">Category</label>
            <select className="form-select form-select-sm" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
              <option value="">All</option>
              {allCategories.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          {/* Date Range */}
          <div className="col-md-2 col-6">
            <label className="form-label small fw-semibold mb-1">From Date</label>
            <input type="date" className="form-control form-control-sm" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </div>
          <div className="col-md-2 col-6">
            <label className="form-label small fw-semibold mb-1">To Date</label>
            <input type="date" className="form-control form-control-sm" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
          {/* Search */}
          <div className="col-md-3 col-6">
            <label className="form-label small fw-semibold mb-1">Search</label>
            <input className="form-control form-control-sm" placeholder="🔍 Remarks, from/to, ref..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="col-md-auto">
            {(filterProject || filterType !== "All" || filterCategory || dateFrom || dateTo || search) && (
              <button className="btn btn-sm btn-outline-danger mt-1"
                onClick={() => { setFilterProject(""); setFilterType("All"); setFilterCategory(""); setDateFrom(""); setDateTo(""); setSearch(""); }}>
                <i className="bi bi-x-circle me-1"></i>Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="card" style={{ overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table className="table table-hover mb-0" style={{ minWidth: 900 }}>
            <thead style={{ background: "#3e2723", color: "#f5f5dc" }}>
              <tr>
                <th className="px-3 py-3" style={{ width: 40 }}>#</th>
                <th style={{ width: 100 }}>Date</th>
                <th>Project</th>
                <th style={{ width: 100 }}>Type</th>
                <th>Category</th>
                <th>From / To</th>
                <th className="text-end" style={{ width: 120 }}>Amount IN</th>
                <th className="text-end" style={{ width: 120 }}>Amount OUT</th>
                <th>Mode</th>
                <th>Remarks</th>
                <th className="text-center" style={{ width: 100 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center py-5 text-muted">
                    <i className="bi bi-receipt" style={{ fontSize: 36, display: "block", marginBottom: 8 }}></i>
                    No transactions found
                  </td>
                </tr>
              ) : (
                <>
                  {filtered.map((t, i) => (
                    <tr key={t.id} style={{ verticalAlign: "middle" }}>
                      <td className="px-3 text-muted small">{i + 1}</td>
                      <td>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {new Date(t.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" })}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#3e2723", maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {t.projectName}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${t.type === "Cash In" ? "bg-success" : "bg-danger"}`} style={{ fontSize: 11 }}>
                          <i className={`bi ${t.type === "Cash In" ? "bi-arrow-down" : "bi-arrow-up"} me-1`}></i>
                          {t.type === "Cash In" ? "IN" : "OUT"}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: 12, background: "#f5f5dc", padding: "2px 8px", borderRadius: 12, color: "#5d4037", fontWeight: 600 }}>
                          {t.category}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{t.fromTo}</div>
                        {t.reference && <small className="text-muted">{t.reference}</small>}
                      </td>
                      {/* Amount IN */}
                      <td className="text-end">
                        {t.type === "Cash In" ? (
                          <span style={{ fontWeight: 700, color: "#2e7d32", fontSize: 14 }}>{formatINR(t.amount)}</span>
                        ) : <span className="text-muted">—</span>}
                      </td>
                      {/* Amount OUT */}
                      <td className="text-end">
                        {t.type === "Cash Out" ? (
                          <span style={{ fontWeight: 700, color: "#c62828", fontSize: 14 }}>{formatINR(t.amount)}</span>
                        ) : <span className="text-muted">—</span>}
                      </td>
                      <td>
                        <span style={{ fontSize: 11, background: "#eeeeee", padding: "2px 8px", borderRadius: 10, color: "#555", fontWeight: 600 }}>
                          {t.paymentMode}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: 12, color: "#666", maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {t.remarks || "—"}
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="d-flex gap-1 justify-content-center">
                          <button className="btn btn-sm btn-outline-info" title="View" onClick={() => setViewTxn(t)}>
                            <i className="bi bi-eye"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-warning" title="Edit" onClick={() => handleEdit(t)}>
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-danger" title="Delete" onClick={() => setDeleteConfirm(t.id)}>
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {/* Totals Row */}
                  <tr style={{ background: "#3e2723", color: "#f5f5dc", fontWeight: 700 }}>
                    <td colSpan={6} className="px-3 py-2">
                      <i className="bi bi-calculator me-2"></i>
                      Total ({filtered.length} transactions)
                    </td>
                    <td className="text-end" style={{ color: "#a5d6a7", fontSize: 15 }}>{formatINR(totalIn)}</td>
                    <td className="text-end" style={{ color: "#ef9a9a", fontSize: 15 }}>{formatINR(totalOut)}</td>
                    <td colSpan={3} className="text-end pe-3">
                      <span style={{ color: balance >= 0 ? "#a5d6a7" : "#ef9a9a" }}>
                        Net: {formatINR(balance)}
                      </span>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Modal */}
      {viewTxn && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div className="card p-4" style={{ maxWidth: 500, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <span className={`badge ${viewTxn.type === "Cash In" ? "bg-success" : "bg-danger"} mb-1`}>
                  <i className={`bi ${viewTxn.type === "Cash In" ? "bi-arrow-down" : "bi-arrow-up"} me-1`}></i>
                  {viewTxn.type}
                </span>
                <h5 className="gold-text mb-0">{formatINR(viewTxn.amount)}</h5>
                <small className="text-muted">{viewTxn.category}</small>
              </div>
              <button className="btn btn-sm btn-light" onClick={() => setViewTxn(null)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="row g-3">
              {[
                { label: "Date", value: new Date(viewTxn.date).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" }), icon: "bi-calendar3" },
                { label: "Project", value: viewTxn.projectName, icon: "bi-building" },
                { label: viewTxn.type === "Cash In" ? "Received From" : "Paid To", value: viewTxn.fromTo, icon: "bi-person" },
                { label: "Payment Mode", value: viewTxn.paymentMode, icon: "bi-credit-card" },
                { label: "Reference No.", value: viewTxn.reference || "—", icon: "bi-hash" },
              ].map((item, i) => (
                <div className="col-6" key={i}>
                  <small className="text-muted d-block"><i className={`bi ${item.icon} me-1`}></i>{item.label}</small>
                  <strong>{item.value}</strong>
                </div>
              ))}
              {viewTxn.remarks && (
                <div className="col-12">
                  <small className="text-muted d-block"><i className="bi bi-chat-text me-1"></i>Remarks</small>
                  <strong>{viewTxn.remarks}</strong>
                </div>
              )}
            </div>
            <div className="text-end mt-3">
              <button className="btn btn-warning btn-sm me-2" onClick={() => { setViewTxn(null); handleEdit(viewTxn); }}>
                <i className="bi bi-pencil me-1"></i>Edit
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setViewTxn(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="card p-4" style={{ maxWidth: 360, width: "90%", textAlign: "center" }}>
            <i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: 40, marginBottom: 12 }}></i>
            <h5>Delete Transaction?</h5>
            <p className="text-muted mb-4">This will permanently remove this record.</p>
            <div className="d-flex gap-2 justify-content-center">
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransactionMaster;
